import React from "react";
import { SWReactIcons } from "@similarweb/icons";
import { PlainTooltip } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import type { ListRecommendationType, OpportunityListType } from "../../../types";
import { StyledRecommendationContainer, StyledRecommendationRefreshButtonWrapper } from "./styles";
import { CloseContainer } from "pro-features/pages/workspace/common components/RightBar/src/RightBar";
import { IconButton } from "@similarweb/ui-components";
import {
    RecommendationsSidebarContentWrapper,
    RecommendationsSidebarHeader,
    RecommendationsSidebarInfo,
    RecommendationsSidebarSubtitle,
    RecommendationsSidebarTitle,
    RecommendationsSidebarTitleWrapper,
    RecommendationsSidebarTopSection,
    Separator,
} from "pro-features/pages/workspace/common components/RecommendationsSidebar/StyledComponents";
import RecommendationsContent from "./RecommendationsContent";
import RecommendationsEmpty from "./RecommendationsEmpty";
import RecommendationsLoader from "./RecommendationsLoader";

type RecommendationsSidebarProps = {
    list: OpportunityListType;
    recommendations: ListRecommendationType[];
    recommendationsBarOpen: boolean;
    recommendationsFetching: boolean;
    toggleRecommendationsBar(isOpen: boolean): void;
    fetchListRecommendations(id: OpportunityListType["opportunityListId"]): void;
    addRecommendationsToList(list: OpportunityListType, domains: string[]): void;
    dismissRecommendation(id: OpportunityListType["opportunityListId"], domain: string): void;
    onNavigateToAnalysisClick<P extends { key: string }>(params: P): void;
};

const RecommendationsSidebar = (props: RecommendationsSidebarProps) => {
    const {
        list,
        recommendations,
        recommendationsBarOpen,
        recommendationsFetching,
        fetchListRecommendations,
        toggleRecommendationsBar,
        dismissRecommendation,
        addRecommendationsToList,
        onNavigateToAnalysisClick,
    } = props;

    const refreshRecommendations = React.useCallback(() => {
        fetchListRecommendations(list.opportunityListId);
    }, [list.opportunityListId]);

    const handleRecommendationDismiss = React.useCallback(
        (domain: string) => {
            dismissRecommendation(list.opportunityListId, domain);
        },
        [list.opportunityListId, dismissRecommendation],
    );

    const handleRecommendationsAdd = React.useCallback(
        (domains: { Domain: string }[]) => {
            addRecommendationsToList(
                list,
                domains.map((d) => d.Domain),
            );
        },
        [list],
    );

    const navigateToAccountReview = (domain: string) => {
        onNavigateToAnalysisClick({
            key: domain,
            country: list.country,
            isWWW: "*",
            duration: "1m",
            webSource: "Total",
        });
    };

    React.useEffect(() => {
        fetchListRecommendations(list.opportunityListId);
    }, [list.opportunityListId, list.country]);

    const renderContent = () => {
        if (recommendationsFetching) {
            return <RecommendationsLoader />;
        }

        if (recommendations.length === 0) {
            return (
                <RecommendationsEmpty
                    list={list}
                    onRefresh={refreshRecommendations}
                />
            );
        }

        return (
            <RecommendationsContent
                list={list}
                recommendations={recommendations}
                onLinkClick={navigateToAccountReview}
                onRecommendationsAdd={handleRecommendationsAdd}
                onRecommendationDismiss={handleRecommendationDismiss}
            />
        );
    };

    return (
        <StyledRecommendationContainer isOpen={recommendationsBarOpen}>
            <CloseContainer isPrimaryOpen={recommendationsBarOpen}>
                <IconButton
                    type="flat"
                    iconSize="xs"
                    iconName="clear"
                    onClick={() => toggleRecommendationsBar(false)}
                />
            </CloseContainer>
            <RecommendationsSidebarTopSection>
                <RecommendationsSidebarHeader>
                    <RecommendationsSidebarTitleWrapper>
                        <RecommendationsSidebarTitle>
                            {translate("workspace.recommendation_sidebar.title")}
                        </RecommendationsSidebarTitle>
                        <PlainTooltip tooltipContent={translate("workspace.recommendation_sidebar.title.tooltip")}>
                            <RecommendationsSidebarInfo>
                                <SWReactIcons
                                    iconName="info"
                                    size="xs"
                                />
                            </RecommendationsSidebarInfo>
                        </PlainTooltip>
                        <StyledRecommendationRefreshButtonWrapper>
                            <IconButton
                                type="flat"
                                iconName="refresh"
                                isDisabled={recommendationsFetching}
                                onClick={refreshRecommendations}
                            />
                        </StyledRecommendationRefreshButtonWrapper>
                    </RecommendationsSidebarTitleWrapper>
                </RecommendationsSidebarHeader>
                <RecommendationsSidebarSubtitle>
                    {translate("workspace.recommendation_sidebar.subtitle.first")}
                </RecommendationsSidebarSubtitle>
            </RecommendationsSidebarTopSection>
            <Separator />
            <RecommendationsSidebarContentWrapper>{renderContent()}</RecommendationsSidebarContentWrapper>
        </StyledRecommendationContainer>
    );
};

export default RecommendationsSidebar;
